/**
 * Client-side split-tree layout helpers (mirrors server profile-layout rules).
 */

export const MAX_WIDGET_SECTIONS = 3;
export const MAX_SECTIONS = 10;
export const MIN_SECTIONS = 1;
export const MAX_SPLIT_DEPTH = 8;

export const ALLOWED_SECTION_TYPES = new Set(['posts', 'widget']);

export function createLeaf(section = null) {
  return { type: 'leaf', section };
}

export function createSplit(direction, first, second) {
  return { type: 'split', direction, children: [first, second] };
}

export function defaultSplitLayout(richTextWidgetId = null) {
  if (richTextWidgetId) {
    return {
      version: 2,
      root: createSplit(
        'col',
        createLeaf({ id: 'richtext', type: 'widget', widgetId: richTextWidgetId }),
        createLeaf({ id: 'posts', type: 'posts' })
      )
    };
  }
  return {
    version: 2,
    root: createSplit(
      'col',
      createLeaf({ id: 'posts', type: 'posts' }),
      createLeaf(null)
    )
  };
}

export function migrateToV2(doc) {
  if (doc?.version === 2 && doc.root) {
    const sections = collectSections(doc.root);
    if (sections.some((s) => s.type === 'about')) {
      return defaultSplitLayout();
    }
    return structuredClone(doc);
  }
  return defaultSplitLayout();
}

/** @deprecated use migrateToV2 */
export function migrateLegacyLayout(doc) {
  return migrateToV2(doc);
}

export function collectSections(root) {
  const sections = [];
  function walk(node) {
    if (!node) return;
    if (node.type === 'leaf') {
      if (node.section) sections.push(node.section);
      return;
    }
    if (node.type === 'split' && Array.isArray(node.children)) {
      node.children.forEach(walk);
    }
  }
  walk(root);
  return sections;
}

export function pathKey(path) {
  return path.join('.');
}

export function getNodeAt(root, path) {
  let node = root;
  for (const index of path) {
    if (!node || node.type !== 'split' || !node.children?.[index]) {
      return null;
    }
    node = node.children[index];
  }
  return node;
}

function cloneNode(node) {
  if (!node) return null;
  if (node.type === 'leaf') {
    return createLeaf(node.section ? { ...node.section } : null);
  }
  return createSplit(
    node.direction,
    cloneNode(node.children[0]),
    cloneNode(node.children[1])
  );
}

export function cloneLayout(layout) {
  return {
    version: 2,
    root: cloneNode(layout.root)
  };
}

function setNodeAt(root, path, newNode) {
  if (!path.length) return newNode;
  const cloned = cloneNode(root);
  let parent = cloned;
  for (let i = 0; i < path.length - 1; i += 1) {
    parent = parent.children[path[i]];
  }
  parent.children[path[path.length - 1]] = newNode;
  return cloned;
}

export function splitNode(root, path, direction) {
  const node = getNodeAt(root, path);
  if (!node || node.type !== 'leaf') return null;

  const split = createSplit(
    direction,
    cloneNode(node),
    createLeaf(null)
  );
  return setNodeAt(root, path, split);
}

export function assignSection(root, path, section) {
  const node = getNodeAt(root, path);
  if (!node || node.type !== 'leaf') return null;
  return setNodeAt(root, path, createLeaf({ ...section }));
}

export function clearSection(root, path) {
  const node = getNodeAt(root, path);
  if (!node || node.type !== 'leaf') return null;
  return setNodeAt(root, path, createLeaf(null));
}

export function updateSection(root, path, patch) {
  const node = getNodeAt(root, path);
  if (!node || node.type !== 'leaf' || !node.section) return null;
  return setNodeAt(root, path, createLeaf({ ...node.section, ...patch }));
}

/**
 * Collapse a split into the first non-empty leaf, or first leaf if both empty.
 */
export function mergeSplit(root, path) {
  const node = getNodeAt(root, path);
  if (!node || node.type !== 'split') return null;

  const [first, second] = node.children;
  const pick = first.section ? first : second.section ? second : first;
  return setNodeAt(root, path, cloneNode(pick));
}

export function countType(root, type) {
  return collectSections(root).filter((s) => s.type === type).length;
}

export function nextWidgetId(root) {
  const widgets = collectSections(root).filter((s) => s.type === 'widget');
  return `widget-${widgets.length + 1}`;
}

export function validateLayoutClient(layout) {
  const sections = collectSections(layout.root);
  if (sections.length > MAX_SECTIONS) {
    return { valid: false, error: `At most ${MAX_SECTIONS} blocks allowed` };
  }
  const widgets = sections.filter((s) => s.type === 'widget');
  if (widgets.length > MAX_WIDGET_SECTIONS) {
    return { valid: false, error: `At most ${MAX_WIDGET_SECTIONS} widgets allowed` };
  }
  const ids = new Set();
  for (const section of sections) {
    if (ids.has(section.id)) {
      return { valid: false, error: `Duplicate block id: ${section.id}` };
    }
    ids.add(section.id);
  }
  return { valid: true };
}

export function labelForType(type, palette) {
  const entry = palette.find((p) => p.type === type);
  return entry ? `${entry.icon || ''} ${entry.label}`.trim() : type;
}
